import { addSlideFooter } from '../slide_footer';
import { READ_AS_TEXT, TITLE_TEXT } from '../constants';
import { createCanvasFromElement } from '../../../../../../../../../../../pages/Report/components/Tab/components/Visualizations/utilities/tree_helpers';

/**
 * Cleans up the SVG string by removing unnecessary elements
 * @param {string} svgString
 * @returns {string}
 */
const cleanSVGString = (svgString) => {
  // Remove XML declaration, DOCTYPE, comments, and unnecessary whitespace
  let cleaned = svgString.replace(/^<\?xml[^>]*\?>/, '').replace(/<!DOCTYPE[^>]*>/i, '');

  let previousLength;
  do {
    previousLength = cleaned.length;
    cleaned = cleaned.replaceAll(/<!--[\s\S]*?-->/g, '');
  } while (cleaned.length !== previousLength);

  return cleaned.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();
};

/**
 * Converts a div element to a Base64-encoded PNG image
 * @param {HTMLElement} element
 * @returns {Promise<string|null>}
 */
const convertElementToPNGBase64Url = async (element) => {
  if (!element) {
    return null;
  }

  try {
    const canvas = await createCanvasFromElement(element);
    const base64Url = canvas.toDataURL('image/png');

    return base64Url;
  } catch (error) {
    console.error('Error converting div to PNG:', error);
    return null;
  }
};

/**
 * Converts an SVG chart element to a Base64-encoded URL
 * @param {Object} chart
 * @returns {string|null}
 */
const convertSVGtoBase64Url = (chart) => {
  try {
    if (!chart || typeof chart.getSVG !== 'function') return null;
    const svgString = chart?.getSVG();

    if (!svgString || typeof svgString !== 'string') {
      return null;
    }

    const cleanedSVGString = cleanSVGString(svgString);
    const base64Data = btoa(cleanedSVGString);
    return `data:image/svg+xml;base64,${base64Data}`;
  } catch (error) {
    console.error('Error converting SVG to Base64:', error);
    return null;
  }
};

/**
 * Converts a chart element to a Base64-encoded image
 * @param {Object|HTMLElement} chart
 * @returns {Promise<string|null>}
 */
const generateChartImageUrl = async (chart) => {
  if (!chart?.current) return null;
  return isHighchartsComponent(chart)
    ? convertSVGtoBase64Url(chart.current?.chart)
    : await convertElementToPNGBase64Url(chart.current);
};

/**
 * Checks if the chart is a Highcharts component
 * @param {Object|HTMLElement} chart
 * @returns {Object|undefined}
 */
const isHighchartsComponent = (chart) => {
  return chart?.current?.chart;
};

/**
 * Gets appropriate chart options based on chart type
 * @param {Object} chart
 * @returns {Object}
 */
const getChartOptions = (chart) => {
  const isHighcharts = isHighchartsComponent(chart);

  return {
    h: isHighcharts ? 4.01 : 3.9,
    w: isHighcharts ? 7.0 : 3.9,
    x: isHighcharts ? 0.5 : 2.3,
    y: 1.0,
  };
};

/**
 * Creates image slide
 * @param {String} brokenBy
 * @param {Object} chart
 * @param {Object} pptx
 * @param {String} readAs
 * @param {Array} reportDateRange
 * @param {String} reportType
 * @param {String} reportUrl
 * @param {String} slideTitle
 * @returns {Object}
 */
export const createImageSlide = async ({
  brokenBy,
  chart,
  pptx,
  readAs,
  reportDateRange,
  reportType,
  reportUrl,
  slideTitle,
}) => {
  // chart.current.chart is a highchart component and chart.current a HTMLElement
  if (!chart?.current) return null;

  const slide = pptx.addSlide();

  slide.addText(slideTitle || `Key Metrics ${brokenBy}`, {
    ...TITLE_TEXT,
    fontSize: slideTitle ? 14 : TITLE_TEXT.fontSize,
  });
  if (readAs) slide.addText(readAs, { ...READ_AS_TEXT, w: '20%', x: 7.5 });

  const chartOptions = getChartOptions(chart);
  try {
    const imageUrl = await generateChartImageUrl(chart);

    if (!imageUrl) {
      throw new Error('Failed to generate chart image URL');
    }

    slide.addImage({
      data: imageUrl,
      ...chartOptions,
    });
  } catch (e) {
    console.error(e);
    slide.addText('Chart image could not be created', chartOptions);
  }

  addSlideFooter({
    pptx,
    reportDateRange,
    reportType,
    reportUrl,
    slide,
  });

  return slide;
};
